import { getOfflineQueue, getOfflineQueueItem, removeFromOfflineQueue, updateOfflineQueueItem } from './db'
import { createAnalysis, type CreateAnalysisMetadata } from './create-analysis'
import { logger } from './monitoring'

// Flips pre-intent `offlineQueue` entries from prior sessions into
// `createAnalysis` + intents, so the legacy OfflineQueueManager can eventually
// be retired. The status→'processing' flip is a claim: the legacy manager
// ignores 'processing' rows, so we don't double-process. Items with
// `uploadedAnalysisId` (partial upload) or `ticketData` (DefectTicketModal
// scope, Phase 8) stay on the legacy path.

let ranOnce = false

export async function drainLegacyOfflineQueue(): Promise<{ migrated: number; skipped: number }> {
  if (ranOnce) return { migrated: 0, skipped: 0 }
  ranOnce = true

  let migrated = 0
  let skipped = 0

  let candidates: Awaited<ReturnType<typeof getOfflineQueue>>
  try {
    candidates = await getOfflineQueue()
  } catch (err) {
    logger.intents.warn('[legacy-drain] getOfflineQueue failed', { error: String(err) })
    return { migrated: 0, skipped: 0 }
  }

  for (const meta of candidates) {
    const unmigratable = (meta.status !== 'pending' && meta.status !== 'error')
      || !!meta.uploadedAnalysisId
      || !!meta.ticketData
    if (unmigratable) { skipped++; continue }

    const id = meta.id!
    try {
      await updateOfflineQueueItem(id, { status: 'processing', error: undefined })
    } catch {
      skipped++
      continue
    }

    try {
      const full = await getOfflineQueueItem(id)
      if (!full || !(full.image instanceof ArrayBuffer) || full.image.byteLength === 0) {
        await removeFromOfflineQueue(id)
        skipped++
        continue
      }

      const blob = new Blob([full.image], { type: full.imageType || 'image/jpeg' })
      const isManual = full.settings?.manual === true

      await createAnalysis(blob, {
        projectId: full.settings?.projectId,
        projectName: full.projectName,
        locationDetails: full.locationDetails,
        inspectionId: full.inspectionId,
        roomId: full.roomId,
        roomLabel: full.roomLabel,
        mode: (full.settings?.mode as CreateAnalysisMetadata['mode']) || 'gemini_vision',
        geminiModel: full.settings?.geminiModel,
        confidence: full.settings?.confidence,
        manual: isManual,
        skipAnalyze: isManual,
      })

      await removeFromOfflineQueue(id)
      migrated++
    } catch (err) {
      logger.intents.warn('[legacy-drain] migration failed; leaving for legacy manager', { id, error: String(err) })
      try { await updateOfflineQueueItem(id, { status: 'pending' }) } catch { /* noop */ }
      skipped++
    }
  }

  if (migrated > 0) {
    logger.intents.info('[legacy-drain] done', { migrated, skipped })
  }

  return { migrated, skipped }
}
